"use client";

import React, { ReactNode } from "react";

import { ApolloProvider } from "@apollo/client";

import BodyClassManager from "@/components/common/BodyClassManager/BodyClassManager";
import { ContentfulLivePreviewGate } from "@/components/common/ContentfulLivePreview/ContentfulLivePreviewGate";
import { ExperimentHandler } from "@/components/common/ExperimentHandler/ExperimentHandler";
import { AdaEmbedInitializer } from "@/components/vendor/Ada";
import { AnalyticsTracker } from "@/components/vendor/Analytics";
import { AppInitializer } from "@/components/vendor/AppInitializer/AppInitializer";
import { KlaviyoFocusManager } from "@/components/vendor/Klaviyo/KlaviyoFocusManager";
import { NewRelicBrowserInitializer } from "@/components/vendor/NewRelic/NewRelicBrowserInitializer";
import { VwoSegmentIntegration } from "@/components/vendor/Vwo";
import { WebVitalsReporter } from "@/components/vendor/WebVitals";
import { AtcAreaConditionalLayoutProvider } from "@/context/AtcAreaConditionalLayoutProvider";
import { CartProvider } from "@/context/CartContext";
import { NavProvider } from "@/context/NavContext";
import { PreviousPathnameProvider } from "@/context/PreviousPathnameContext";
import { SearchProvider } from "@/context/SearchContext";
import { UserProvider } from "@/context/UserContext";
import client from "@/lib/apolloMagentoClient";
import { ContentfulProduct } from "@/types/common";

const ENABLE_CONTENTFUL_LIVE_PREVIEW = process.env.NEXT_PUBLIC_ACTIVE_ENV !== "production";

interface ClientLayoutProps {
  children: ReactNode;
  contentfulProducts: ContentfulProduct[];
}

const ClientLayout = ({ children, contentfulProducts }: ClientLayoutProps) => {
  const app = (
    <ApolloProvider client={client}>
      <AppInitializer />
      <KlaviyoFocusManager />
      <AdaEmbedInitializer />
      <BodyClassManager />
      <AnalyticsTracker />
      <ExperimentHandler />
      <NewRelicBrowserInitializer />
      <WebVitalsReporter />
      <VwoSegmentIntegration />
      <UserProvider>
        <AtcAreaConditionalLayoutProvider>
          <CartProvider>
            <NavProvider>
              <SearchProvider contentfulProducts={contentfulProducts}>{children}</SearchProvider>
            </NavProvider>
          </CartProvider>
        </AtcAreaConditionalLayoutProvider>
      </UserProvider>
    </ApolloProvider>
  );

  if (!ENABLE_CONTENTFUL_LIVE_PREVIEW) {
    return <PreviousPathnameProvider>{app}</PreviousPathnameProvider>;
  }

  return (
    <PreviousPathnameProvider>
      <ContentfulLivePreviewGate>{app}</ContentfulLivePreviewGate>
    </PreviousPathnameProvider>
  );
};

export default ClientLayout;
